import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import * as Styled from './MTimer.styles';
import { MTimerProps, MTimerAlignment, TimeLeft } from './MTimer.types';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { ADigit } from '@marriott/mi-web-ui-kit';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ADigitSize } from '@marriott/mi-web-ui-kit';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ABody } from '@marriott/mi-web-ui-kit';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ABodySize } from '@marriott/mi-web-ui-kit';

const COMPONENT_NAME = 'm-cobrand-MTimer';
const TEST_ID = 'cobrand-MTimer';

const calculateTimeLeft = (targetDate: string): TimeLeft => {
  const now = new Date().getTime();
  const target = new Date(targetDate).getTime();
  const difference = target - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
};

export const MTimer: FC<MTimerProps> = ({
  alignment = MTimerAlignment.Centered,
  aDigit,
  aBody,
  className,
  targetDate,
  daysLabel = 'DAYS',
  hoursLabel = 'HOURS',
  minutesLabel = 'MINUTES',
  secondsLabel = 'SECONDS',
  updateInterval = 1000,
  screenReaderInterval = 300000, // 5 minutes
  onComplete,
  isAuthorMode = false,
  isLoading = false,
  ...rest
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    targetDate ? calculateTimeLeft(targetDate) : { days: 0, hours: 0, minutes: 0, seconds: 0 }
  );

  // Initialize announcement with timer only (description and button read naturally from DOM)
  const [announcementText, setAnnouncementText] = useState(() => {
    if (!targetDate) return '';

    const initialTime = calculateTimeLeft(targetDate);
    return `${initialTime.days} days, ${initialTime.hours} hours, ${initialTime.minutes} minutes remaining`;
  });

  const [isClient, setIsClient] = useState(false);

  // Use refs to avoid recreating updateTime callback
  const lastAnnouncementRef = useRef(0);
  const hasCompletedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const isFirstAnnouncementRef = useRef(true);

  // Keep the ref up to date
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Hydration fix: Detect client-side rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  const updateTime = useCallback(() => {
    if (!targetDate) return;

    const newTimeLeft = calculateTimeLeft(targetDate);
    setTimeLeft(newTimeLeft);

    // Check if countdown is complete
    if (newTimeLeft.days === 0 && newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
      if (onCompleteRef.current && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onCompleteRef.current();
      }
    }

    // Update screen reader announcement every 5 minutes with timer only
    const now = Date.now();

    // Skip the very first call since we already set the announcement in initial state
    if (isFirstAnnouncementRef.current) {
      isFirstAnnouncementRef.current = false;
      lastAnnouncementRef.current = now;
      return;
    }

    const shouldAnnounce = now - lastAnnouncementRef.current >= screenReaderInterval;

    if (shouldAnnounce) {
      // All announcements only include timer (description and button read naturally from DOM)
      const timeLeftText = `${newTimeLeft.days} days, ${newTimeLeft.hours} hours, ${newTimeLeft.minutes} minutes remaining`;
      setAnnouncementText(timeLeftText);
      lastAnnouncementRef.current = now;
    }
  }, [targetDate, screenReaderInterval]);

  useEffect(() => {
    if (!isClient || isAuthorMode || !targetDate) {
      // Don't start countdown until client-side or if in author mode or no targetDate
      return;
    }

    // Reset completion flag and first announcement flag when targetDate changes
    hasCompletedRef.current = false;
    isFirstAnnouncementRef.current = true;

    updateTime();
    const interval = setInterval(updateTime, updateInterval);

    return () => clearInterval(interval);
  }, [updateTime, updateInterval, isAuthorMode, isClient, targetDate]);

  const formatNumber = (num: number): string => String(num).padStart(2, '0');

  // If no targetDate, render static display (backward compatible)
  if (!targetDate) {
    const alignmentClass = alignment === MTimerAlignment.Centered ? 'align-items-center' : 'align-items-start';

    return (
      <Styled.Wrapper
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        alignment={alignment}
        className={`d-flex flex-column ${alignmentClass}${className ? ` ${className}` : ''}`}
        {...rest}
      >
        {aDigit && <ADigit {...aDigit} size={aDigit.size ?? ADigitSize.Large} className="pb-0" />}
        {aBody?.bodyText ? <ABody {...aBody} /> : null}
      </Styled.Wrapper>
    );
  }

  // Show skeleton loader during SSR to avoid hydration mismatch OR when explicitly loading
  if (!isClient || isLoading) {
    return (
      <Styled.SkeletonWrapper
        data-component-name={COMPONENT_NAME}
        data-testid={`${TEST_ID}-skeleton`}
        className={className}
      >
        <Styled.TimerDisplay>
          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>

          <Styled.Separator>:</Styled.Separator>

          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>

          <Styled.Separator>:</Styled.Separator>

          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>

          <Styled.Separator>:</Styled.Separator>

          <Styled.TimerUnit>
            <Styled.SkeletonDigit>
              <Skeleton
                width={30}
                height={30}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonDigit>
            <Styled.SkeletonLabel>
              <Skeleton
                width={50}
                height={12}
                baseColor="var(--t-neutral-color-10, #ebebeb)"
                highlightColor="var(--t-neutral-color-05, #f5f5f5)"
              />
            </Styled.SkeletonLabel>
          </Styled.TimerUnit>
        </Styled.TimerDisplay>
      </Styled.SkeletonWrapper>
    );
  }

  // Render countdown display
  return (
    <Styled.Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} className={className}>
      {/* Visual timer - completely hidden from screen readers to avoid duplication */}
      <Styled.TimerDisplay aria-hidden="true">
        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.days)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={daysLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>

        <Styled.Separator>:</Styled.Separator>

        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.hours)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={hoursLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>

        <Styled.Separator>:</Styled.Separator>

        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.minutes)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={minutesLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>

        <Styled.Separator>:</Styled.Separator>

        <Styled.TimerUnit>
          <ADigit text={formatNumber(timeLeft.seconds)} size={ADigitSize.Large} className="pb-0" />
          <ABody bodyText={secondsLabel} size={ABodySize.XS} />
        </Styled.TimerUnit>
      </Styled.TimerDisplay>

      {/* Screen reader announcement - updated every 60 seconds, no seconds to avoid interruption */}
      <Styled.ScreenReaderOnly aria-live="polite" aria-atomic="true">
        {announcementText}
      </Styled.ScreenReaderOnly>
    </Styled.Wrapper>
  );
};

export { MTimerAlignment, TEST_ID as M_TIMER_TEST_ID };
